import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { ValidateRewardFailureCode } from '@sg/graphql-schema';
import { type Order } from '@sg/graphql-schema';

import {
  type ApplyRewardParams,
  type ApplyRewardResponse,
  type RemoveRewardParams,
  type RemoveRewardResponse,
} from '@order/features-ordering';
import { useTelemetry } from '@order/Telemetry';

import {
  ApplyBagRewardDocument,
  type ApplyBagRewardMutation,
  type ApplyBagRewardMutationVariables,
  RemoveBagRewardDocument,
  type RemoveBagRewardMutation,
  type RemoveBagRewardMutationVariables,
} from '../../GraphQL/BagRewards.generated';
import { prepareCart } from '../../utils';
import { useApplyRewardErrorMessage } from './useApplyRewardErrorMessage';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching, apply and removing bag rewards.
 */
export const useRewards = () => {
  const client = useClient();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const getApplyRewardErrorMessage = useApplyRewardErrorMessage();

  // ─── Mutations ────────────────────────────────────────────────────────────

  const applyReward = useMemo(
    () =>
      fromPromise<ApplyRewardResponse, ApplyRewardParams>(async ({ input }) => {
        const mutation = client.mutation<ApplyBagRewardMutation, ApplyBagRewardMutationVariables>;

        const response = await mutation(
          ApplyBagRewardDocument,
          { input },
          { requestPolicy: 'network-only' },
        ).toPromise();

        const result = response.data?.applyReward;

        if (result?.__typename === 'ApplyRewardSuccess') {
          return { cart: prepareCart(result.order as Partial<Order>) };
        }

        const error = getApplyRewardErrorMessage(result, response.error);

        track('bag.apply-reward.failed', { userError: error });

        if (result?.__typename === 'RewardNotApplied') {
          const failureReasons = result?.failureReasons ?? [];
          const isRewardMissingSku = failureReasons.includes(ValidateRewardFailureCode.MissingSku);

          return { error, isRewardMissingSku };
        }

        return { error, isRewardMissingSku: false };
      }),
    [client.mutation, getApplyRewardErrorMessage, track],
  );

  const removeReward = useMemo(
    () =>
      fromPromise<RemoveRewardResponse, RemoveRewardParams>(async ({ input }) => {
        const mutation = client.mutation<RemoveBagRewardMutation, RemoveBagRewardMutationVariables>;

        const response = await mutation(
          RemoveBagRewardDocument,
          { input },
          { requestPolicy: 'network-only' },
        ).toPromise();

        const result = response.data?.removeReward;

        if (result?.__typename === 'RemoveRewardSuccess') {
          return { cart: prepareCart(result.order as Partial<Order>) };
        }

        return { error: formatMessage(messages.removeRewardError) };
      }),
    [client.mutation, formatMessage],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { applyReward, removeReward };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  fetchRewardsError: {
    defaultMessage:
      'There was an issue loading your rewards. Please refresh the app or try again later.',
    description: 'Bag | Rewards | Fetch rewards error',
  },
  removeRewardError: {
    defaultMessage: 'Something went wrong removing your rewards.',
    description: 'Bag | Rewards | Remove reward error',
  },
});
